import { Component, EventEmitter, Input, OnChanges, Output, SimpleChanges, ViewChild } from '@angular/core';
import { Yii } from "libraries/interfaces/Yii";
import { ILabels } from "libraries/interfaces/ILabels";
import { IMultiselectConfig, IMultiselectOptions } from "libraries/module/formbuilder/fields/multiselect/multiselect.model";
import { IFieldSpecs } from "libraries/module/formbuilder/fields/interfaces/field-specs";
import { FormBuilder, FormGroup } from "@angular/forms";
import { MatTableDataSource } from "@angular/material/table";
import { NotificationType } from "libraries/interfaces/INotification";
import { CardComponent } from "libraries/ui_card/card.component";
import { DateParserService } from "libraries/service/date/dateParser.service";
import { BookingResourceService } from "libraries/service/resources/booking/bookingResource.service";
import { OperationStatus } from "libraries/service/resources/model/search/IBooking";
import * as moment from 'moment';
import { BookingOperationService, IOperationItem } from "libraries/component/booking-information/services/booking-operation.service";
import { IWenji } from "libraries/interfaces/IWenji";
import { IGetBookingsCommonResponse, IBookingOperation } from 'libraries/service/resources/booking/interfaces/IGetBookings';

import { faAngleDown, faAngleUp, IconDefinition } from "@fortawesome/free-solid-svg-icons";
import {IMomentDateFormat} from "../../../../interfaces/IMomentDateFormat";

declare const wenji: IWenji;

@Component({
    selector: 'booking-operations',
    template: `
        <card #cardContainer [isCollapsible]="true">
            <div class="card-header-content">
                <div class="title" (click)="menuToggleCard()">
                    {{ labels.cardTitle }}
                </div>
                <div *ngIf="cardContainer.showContent && dataSource?.data?.length" class="filter-operation">
                    <multiselect [options]="multiselectOptions"
                                 [fieldConfig]="multiselectFieldConfig"
                                 [labelCustom]="multiselectConfig.defaultLabel"></multiselect>

                </div>
            </div>
            <div class="card-body-content">
                <div *ngIf="!dataSource?.data?.length" class="text-cardBodyContent noInformation">
                    {{labels.noOperation}}
                </div>

                <table mat-table *ngIf="dataSource?.data?.length" [dataSource]="dataSource" multiTemplateDataRows>

                    <ng-container matColumnDef="dateTime">
                        <th mat-header-cell *matHeaderCellDef>{{columnLabels.dateTime}}</th>
                        <td mat-cell *matCellDef="let element">
                            <span class="row-dateTime" [title]="timezone">
                                {{element.dateTime | dateMoment: dateFormats.momentMedium}} - {{element.dateTime | dateMoment: dateFormats.momentTime}}
                            </span>
                            <fa-icon [icon]="element === expandedElement ? iconAngleUp : iconAngleDown" class="icon row-expander"></fa-icon>
                        </td>

                    </ng-container>

                    <ng-container matColumnDef="serviceTitle">
                        <th mat-header-cell *matHeaderCellDef> {{columnLabels.serviceTitle}}</th>
                        <td mat-cell *matCellDef="let element"> {{element.serviceTitle}} </td>
                    </ng-container>

                    <ng-container matColumnDef="result">
                        <th mat-header-cell *matHeaderCellDef> {{columnLabels.result}}</th>
                        <td mat-cell *matCellDef="let element">
                            <booking-operation-result-icon [result]="element.result"></booking-operation-result-icon>
                        </td>
                    </ng-container>

                    <ng-container matColumnDef="expandedDetail">
                        <td mat-cell *matCellDef="let element" [attr.colspan]="displayedColumns.length">
                            <div class="element-detail" *ngIf="element.messages?.length">
                                <notification [type]="getNotificationType(element.result)">
                                    <text-with-children [values]="element.messages"></text-with-children>
                                </notification>
                            </div>
                        </td>
                    </ng-container>

                    <tr mat-header-row *matHeaderRowDef="displayedColumns; sticky: true"></tr>

                    <tr mat-row *matRowDef="let element;  columns: displayedColumns; let i = dataIndex"
                        class="element-row"
                        [class.odd-row]="(i+1) % 2"
                        [class.even-row]="i % 2"
                        [class.expandable-row]="element.messages?.length"
                        [class.collapsed-row]="expandedElement != element"
                        [class.expanded-row]="expandedElement === element"
                        (click)="expandRow(element)">
                    </tr>

                    <tr mat-row *matRowDef="let element; columns: ['expandedDetail']"
                        class="detail-row"
                        [class.collapsed-detail-row]="expandedElement != element"
                        [class.expanded-detail-row]="expandedElement === element"
                    ></tr>

                </table>
            </div>
        </card>
    `,
    styleUrls: ['booking-operations.component.scss']
})
export class BookingOperationsComponent implements OnChanges {

    @Input()
    public bookingResult: IGetBookingsCommonResponse;

    @ViewChild('cardContainer')
    public cardContainer: CardComponent;

    @Output()
    public readonly onCardTitleClick: EventEmitter<string> = new EventEmitter();

    public formGroup: FormGroup;

    public multiselectOptions: IMultiselectOptions[];
    public multiselectFieldConfig: IFieldSpecs;
    public multiselectConfig: IMultiselectConfig;

    public displayedColumns: string[] = ['dateTime', 'result', 'serviceTitle'];
    public dataSource: MatTableDataSource<IOperationItem>;
    public expandedElement: IOperationItem | null;
    public name: string = 'bookingOperations';

    public readonly timezone: string = wenji ? wenji.ui.timezone : null;
    public readonly dateFormats: IMomentDateFormat = JSON.parse(sessionStorage.getItem('dateFormat'));

    public readonly labels: ILabels = {
        cardTitle: Yii.t('wui-shared-js', 'Journal des opérations'),
        noOperation: Yii.t('wui-shared-js', 'Aucune opération à afficher.'),
    };

    public readonly columnLabels: ILabels = {
        dateTime: Yii.t('wui-shared-js', 'Date d\'exécution'),
        serviceTitle: Yii.t('wui-shared-js', 'Nom'),
        result: Yii.t('wui-shared-js', 'Résultat'),
    };

    public iconAngleDown: IconDefinition = faAngleDown;
    public iconAngleUp: IconDefinition = faAngleUp;

    constructor(private readonly fb: FormBuilder,
        private readonly dateService: DateParserService,
        private readonly bookingResourceService: BookingResourceService,
        private readonly bookingOperationService: BookingOperationService) {
    }

    public ngOnChanges(changes: SimpleChanges): void {

        const curBookingResult: IGetBookingsCommonResponse = changes.bookingResult.currentValue;
        if (!curBookingResult) {
            return;
        }
        this.doInit(curBookingResult);
    }

    public doInit(curBookingData: IGetBookingsCommonResponse): void {
        const operationItems: IOperationItem[] = (curBookingData.operations || []).map((bookOper: IBookingOperation): IOperationItem => {
            return {
                serviceCode: bookOper.service.code,
                serviceTitle: bookOper.service.title,
                dateTime: moment(bookOper.timestamp).toDate(),
                result: this.bookingOperationService.getOperationsStatus(bookOper.operations),
                messages: this.bookingOperationService.getOperationItems(bookOper.operations),
            };
        });

        // init formgroup
        this.formGroup = this.fb.group({
            multiselect: this.fb.control(getMultiselectOptions(operationItems).map((opt: IMultiselectOptions) => opt.value))
        });

        // init multiselect
        this.multiselectOptions = getMultiselectOptions(operationItems);
        this.multiselectFieldConfig = this.getMultiselectFieldConfig();
        this.multiselectConfig = getMultiselectConfig();

        // set dataSource
        this.dataSource = new MatTableDataSource<IOperationItem>(operationItems);

        // link datasource and multiselect filter
        this.dataSource.filterPredicate = (oper: IOperationItem, filter: string): boolean => {
            if (!filter) {
                return false;
            }
            return filter.split(';').indexOf(oper.serviceCode) !== -1;
        };
        this.formGroup.valueChanges.subscribe((selected: { multiselect: [string] }) => {
            this.dataSource.filter = selected.multiselect.join(';');
        });
    }

    public getNotificationType(result: OperationStatus): NotificationType {
        return this.bookingOperationService.getNotificationType(result);
    }

    public expandRow(element: IOperationItem): void {
        if (element.messages.length) {
            this.expandedElement = this.expandedElement === element ? null : element;
        }
    }

    public menuToggleCard(): void {
        this.onCardTitleClick.emit(this.name);
    }

    private getMultiselectFieldConfig(): IFieldSpecs {
        return {
            formControlName: "multiselect",
            formGroup: this.formGroup,
        };
    }
}

function getMultiselectOptions(operationItems: IOperationItem[]): IMultiselectOptions[] {
    const result: { [serviceCode: string]: IMultiselectOptions } = {};
    operationItems.forEach((operation: IOperationItem) => {
        result[operation.serviceCode] = { label: operation.serviceTitle, value: operation.serviceCode };
    });
    return Object.values(result);
}

function getMultiselectConfig(): IMultiselectConfig {
    return {
        defaultLabel: Yii.t('wui-shared-js', 'Filtrer sur les services'),
        displaySelectedLabel: false
    };
}
